import { useEffect, useMemo, useState } from "react";

import { Button, Dropdown, MenuProps } from "antd";
import { MenuItemType } from "antd/lib/menu/hooks/useItems";
import { CalculationMethod } from "constants/charts.enums";
import { useChartsContext } from "contexts/ChartContext";
import styled from "styled-components";

import { useChartEntities } from "hooks/charts";

import { createStackedBarCalculationItems } from "./stackedBarCalculationItems";

const Wrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const StyledButton = styled(Button)`
  width: 30px;
  height: 25px;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export interface StackedBarCalculationModel {
  onSelect: (item: CalculationMethod) => void;
}

/**
 * StackedBarCalculation is a custom dropdown component for selecting calculation methods.
 * It displays a button with the currently selected calculation method icon,
 * and a dropdown menu with available calculation methods.
 */
export default function StackedBarCalculation({ onSelect }: StackedBarCalculationModel) {
  const { id, entityKind } = useChartsContext();
  const { calculationMethod } = useChartEntities(id);
  const items: MenuProps["items"] = useMemo(
    () => createStackedBarCalculationItems(entityKind),
    [entityKind]
  );
  const [selectedItem, setSelectedItem] = useState<MenuItemType>(
    items.find(
      (item) => (item.key as CalculationMethod) === CalculationMethod.Count
    ) as MenuItemType
  );
  const handleMenuItemClick: MenuProps["onClick"] = ({ key }) => {
    const foundIndex = items.findIndex((item) => item.key.toString() == key);
    if (foundIndex >= 0) {
      setSelectedItem(items[foundIndex] as MenuItemType);
      onSelect && onSelect(parseInt(key) as CalculationMethod);
    }
  };
  useEffect(() => {
    const matchingItem = items.find(
      (item) => (item.key as CalculationMethod) === calculationMethod.properties.method
    ) as MenuItemType;
    if (matchingItem) {
      setSelectedItem(matchingItem);
    }
  }, [calculationMethod.properties.method]);

  return (
    <Wrapper>
      <Dropdown menu={{ items, onClick: handleMenuItemClick }} trigger={["click"]}>
        <StyledButton
          onClick={(e) => e.preventDefault()}
          data-testid={"stacked-bar-button"}>
          {selectedItem?.icon}
        </StyledButton>
      </Dropdown>
    </Wrapper>
  );
}
